/**
 * UI-facing API types for the Teacher dashboard shell (S1-011).
 *
 * These are intentionally a subset of the shared LMS types — the UI never
 * sees raw students, raw scores, or anything else that could leak PII even
 * if a future fixture changes shape. The vault is server-only.
 */

export type UiRole = 'teacher' | 'admin' | 'student' | 'support';

export interface UiPrincipal {
  userId: string;
  tenantId: string;
  role: UiRole;
}

export interface UiSession {
  token: string;
  principal: UiPrincipal;
  expiresAt: string;
}

export interface UiCourse {
  id: string;
  name: string;
  code: string;
  termName: string;
  studentCount: number;
}

export interface UiAssignment {
  id: string;
  courseId: string;
  title: string;
  dueAt: string;
  pointsPossible: number;
}

export type UiCoursewareStatus = 'draft' | 'needs_review';

export interface UiCoursewareDraft {
  id: string;
  courseId: string;
  courseName: string;
  title: string;
  status: UiCoursewareStatus;
  updatedAt: string;
}

export type UiGradingStatus = 'draft' | 'needs_review';

export interface UiGradingDraft {
  id: string;
  assignmentId: string;
  assignmentTitle: string;
  courseName: string;
  /** Pseudonymous student id, e.g. `Student-001`. The UI never resolves this. */
  studentPseudonym: string;
  status: UiGradingStatus;
  updatedAt: string;
}

export interface UiSettings {
  tenantId: string;
  lmsProvider: 'mock';
  notifications: {
    gradingDraftReady: boolean;
    coursewareDraftReady: boolean;
  };
}

/* ---- Student-side types --------------------------------------------- */

/**
 * Profile shown on the student dashboard. The UI never receives a real
 * name — the student picks a display pseudonym at registration time and
 * everything we render is derived from it.
 */
export interface UiStudentProfile {
  /** Pseudonymous student id, e.g. `Student-001`. */
  pseudonym: string;
  /** Display label the student picked for themselves. Mock-only in MVP. */
  displayName: string;
}

export type UiStudentCourseStatus = 'in_progress' | 'completed' | 'not_started';

export interface UiStudentCourse {
  id: string;
  name: string;
  code: string;
  termName: string;
  /** Percent complete, 0-100, integer. */
  progressPercent: number;
  status: UiStudentCourseStatus;
  /** Number of assignments still due. */
  openAssignments: number;
}

/**
 * Practice problem the tutor walks the student through. Supplied by the
 * mock client today; will move to a learning-safe agent in a later sprint.
 */
export interface UiTutorProblem {
  id: string;
  courseId: string;
  courseName: string;
  title: string;
  /** Plain-language statement of the problem. */
  prompt: string;
  /** Subject tag for filtering, e.g. "math", "history". */
  subject: string;
}

export type UiAssistantMessageRole = 'student' | 'assistant';

/**
 * Single message in the Study Assistant draft transcript. The assistant
 * role's messages are always labelled as drafts in the UI — the agent
 * is not yet shipped, the transcript is illustrative only.
 */
export interface UiAssistantMessage {
  id: string;
  role: UiAssistantMessageRole;
  /** Text the user sees. Already de-identified by the (mock) redactor. */
  content: string;
  /** ISO timestamp. */
  createdAt: string;
}

/**
 * Per-course progress entry on `/student/progress`. Numbers are mock,
 * never sourced from a real LMS in this sprint.
 */
export interface UiStudentProgressEntry {
  courseId: string;
  courseName: string;
  /** Percent complete, 0-100, integer. */
  progressPercent: number;
  assignmentsCompleted: number;
  assignmentsTotal: number;
  /** Last activity ISO timestamp; null if the student has not started. */
  lastActivityAt: string | null;
}

export interface ApiError {
  code: string;
  message: string;
}

export type Outcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: ApiError };

export const ok = <T>(value: T): Outcome<T> => ({ ok: true, value });
export const fail = (code: string, message: string): Outcome<never> => ({
  ok: false,
  error: { code, message },
});
